import EventIcon from "@mui/icons-material/Event";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SecurityIcon from "@mui/icons-material/Security";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../hooks";
import { pluginService } from "../services/PluginService";
import { userService, type Agent } from "../services/UserService";

export const AgentsPage: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pluginSources, setPluginSources] = useState<Array<{ id: string; name: string }>>([]);
  const [resettingAgents, setResettingAgents] = useState<Set<string>>(new Set());

  // Get user authentication
  useAuth();

  const fetchAgents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const agentsData = await userService.getUserAgents();
      console.log("AgentsPage: Received agents data:", agentsData);
      setAgents(agentsData);
    } catch (err) {
      console.error("AgentsPage: Error fetching agents:", err);
      setError("Failed to fetch agents.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPluginSources = async () => {
    try {
      const pluginInfo = await pluginService.getPluginInfo();
      setPluginSources(pluginInfo.sources || []);
    } catch (err) {
      // ignore
    }
  };

  const handleResetPermissions = async (agentId: string) => {
    setResettingAgents((prev) => new Set(prev).add(agentId));
    try {
      await userService.resetAgentPermissions(agentId);
      // Refresh the agents list to show updated permissions
      await fetchAgents();
    } catch (err) {
      console.error("AgentsPage: Error resetting agent permissions:", err);
      setError("Failed to reset agent permissions.");
    } finally {
      setResettingAgents((prev) => {
        const newSet = new Set(prev);
        newSet.delete(agentId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchPluginSources();
  }, []);

  const getAgentTypeColor = (type: string) => {
    switch (type) {
      case "ClassificationAgent":
        return "primary";
      case "PersonalAssistantAgent":
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Your Agents
      </Typography>
      {isLoading && <CircularProgress sx={{ display: "block", my: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!isLoading && !error && agents.length === 0 && <Typography>No agents found.</Typography>}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {agents.map((agent) => (
          <Box key={agent.agentId} sx={{ flex: 1, minWidth: 300 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Typography variant="h6">{agent.name}</Typography>
                  <Chip label={agent.type} color={getAgentTypeColor(agent.type)} size="small" />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Agent ID: {agent.agentId}
                </Typography>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SecurityIcon fontSize="small" />
                      <Typography variant="subtitle2">
                        Permissions ({agent.permissions?.canProposeActions?.length || 0} propose, {agent.permissions?.canExecuteActions?.length || 0} execute)
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Can Propose Actions ({agent.permissions?.canProposeActions?.length || 0}):
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {agent.permissions?.canProposeActions?.length > 0 ? (
                          agent.permissions.canProposeActions.map((action: string) => <Chip key={action} label={action} size="small" color="success" variant="outlined" />)
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Can Execute Actions ({agent.permissions?.canExecuteActions?.length || 0}):
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {agent.permissions?.canExecuteActions?.length > 0 ? (
                          agent.permissions.canExecuteActions.map((action: string) => <Chip key={action} label={action} size="small" color="warning" variant="outlined" />)
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EventIcon fontSize="small" />
                      <Typography variant="subtitle2">Subscribed Events ({agent.permissions?.subscribedEvents?.length || 0})</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {agent.permissions?.subscribedEvents?.length > 0 ? (
                        agent.permissions.subscribedEvents.map((eventType: string) => <Chip key={eventType} label={eventType} size="small" variant="outlined" />)
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No events subscribed
                        </Typography>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Allowed Sources:</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                    {(agent.permissions?.allowedSources || []).length > 0 ? (
                      (agent.permissions?.allowedSources || []).map((sourceId: string) => {
                        const source = pluginSources.find((s: { id: string; name: string }) => s.id === sourceId);
                        return source ? (
                          <Chip key={sourceId} label={source.name} size="small" color="info" variant="outlined" />
                        ) : (
                          <Chip key={sourceId} label={sourceId} size="small" variant="outlined" />
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(agent.createdAt).toLocaleDateString()}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RestartAltIcon />}
                      onClick={() => handleResetPermissions(agent.agentId)}
                      disabled={resettingAgents.has(agent.agentId)}
                      color="warning"
                    >
                      {resettingAgents.has(agent.agentId) ? "Resetting..." : "Reset Permissions"}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
